import {
  TrendingUp as DepositIcon,
  Sync as RefreshIcon,
  RequestQuote as SettlementIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as WithdrawIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  depositToWallet,
  fetchWallet,
  fetchWalletTransactions,
  withdrawFromWallet,
} from "../../../application/actions/financialActions";
import {
  selectTransactionsData,
  selectTransactionsLoading,
  selectWalletData,
  selectWalletLoading,
} from "../../../application/selectors/financial";

const WalletDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const wallet = useSelector(selectWalletData);
  const walletLoading = useSelector(selectWalletLoading);
  const transactions = useSelector(selectTransactionsData);
  const transactionsLoading = useSelector(selectTransactionsLoading);

  // Local State
  const [depositDialog, setDepositDialog] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = () => {
    dispatch(fetchWallet());
    dispatch(fetchWalletTransactions({ pageNumber: 1, pageSize: 100 }));
  };

  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("لطفاً مبلغ معتبر وارد کنید");
      return;
    }

    await dispatch(
      depositToWallet({
        amount: parseFloat(amount),
        description: description || "واریز به کیف پول",
      })
    );

    setDepositDialog(false);
    setAmount("");
    setDescription("");
    loadWalletData();
  };

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("لطفاً مبلغ معتبر وارد کنید");
      return;
    }

    if (wallet && parseFloat(amount) > wallet.balance) {
      toast.error("موجودی کافی نیست");
      return;
    }

    await dispatch(
      withdrawFromWallet({
        amount: parseFloat(amount),
        description: description || "برداشت از کیف پول",
      })
    );

    setWithdrawDialog(false);
    setAmount("");
    setDescription("");
    loadWalletData();
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      Deposit: "واریز",
      Withdraw: "برداشت",
      Payment: "پرداخت",
      Refund: "بازگشت وجه",
      Commission: "کمیسیون",
      Settlement: "تسویه حساب",
      Hold: "مسدود شده",
      Release: "آزاد شده",
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case "Deposit":
      case "Refund":
      case "Release":
        return "success";
      case "Withdraw":
      case "Payment":
      case "Hold":
        return "error";
      case "Commission":
        return "warning";
      case "Settlement":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            کیف پول
          </Typography>
          <Typography variant="body1" color="text.secondary">
            مدیریت موجودی و تراکنش‌های مالی
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadWalletData}
        >
          بروزرسانی
        </Button>
      </Box>

      {/* Balance Cards */}
      {walletLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Total Balance */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <WalletIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6">موجودی کل</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {wallet?.balance?.toLocaleString("fa-IR") || "0"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  ریال
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* On Hold Amount */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <WalletIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6">در انتظار</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {wallet?.onHoldAmount?.toLocaleString("fa-IR") || "0"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  ریال
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Available Balance */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <WalletIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6">قابل برداشت</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {(
                    (wallet?.balance || 0) - (wallet?.onHoldAmount || 0)
                  ).toLocaleString("fa-IR")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  ریال
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<DepositIcon />}
            onClick={() => setDepositDialog(true)}
            sx={{ py: 2 }}
          >
            واریز وجه
          </Button>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<WithdrawIcon />}
            onClick={() => setWithdrawDialog(true)}
            sx={{ py: 2 }}
            color="error"
          >
            برداشت وجه
          </Button>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<SettlementIcon />}
            onClick={() => navigate("/settlements")}
            sx={{ py: 2 }}
            color="success"
          >
            درخواست تسویه
          </Button>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Paper>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h6">تراکنش‌های اخیر</Typography>
        </Box>

        {transactionsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : transactions && transactions.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>تاریخ</strong>
                  </TableCell>
                  <TableCell>
                    <strong>نوع تراکنش</strong>
                  </TableCell>
                  <TableCell>
                    <strong>مبلغ</strong>
                  </TableCell>
                  <TableCell>
                    <strong>توضیحات</strong>
                  </TableCell>
                  <TableCell>
                    <strong>موجودی پس از تراکنش</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {transaction.createdAt &&
                        format(
                          new Date(transaction.createdAt),
                          "yyyy/MM/dd HH:mm"
                        )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTransactionTypeLabel(
                          transaction.transactionType
                        )}
                        color={getTransactionTypeColor(
                          transaction.transactionType
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight="bold"
                        color={
                          transaction.amount >= 0
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        {transaction.amount >= 0 ? "+" : ""}
                        {transaction.amount?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.description || "-"}</TableCell>
                    <TableCell>
                      {transaction.balanceAfter?.toLocaleString("fa-IR")} ریال
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              تراکنشی یافت نشد
            </Typography>
          </Box>
        )}

        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button onClick={() => navigate("/transactions")}>
            مشاهده تمام تراکنش‌ها
          </Button>
        </Box>
      </Paper>

      {/* Deposit Dialog */}
      <Dialog
        open={depositDialog}
        onClose={() => setDepositDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>واریز به کیف پول</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="مبلغ (ریال)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="توضیحات (اختیاری)"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositDialog(false)}>انصراف</Button>
          <Button variant="contained" onClick={handleDeposit}>
            واریز
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog
        open={withdrawDialog}
        onClose={() => setWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>برداشت از کیف پول</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            موجودی قابل برداشت:{" "}
            {(
              (wallet?.balance || 0) - (wallet?.onHoldAmount || 0)
            ).toLocaleString("fa-IR")}{" "}
            ریال
          </Typography>
          <TextField
            fullWidth
            label="مبلغ (ریال)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="توضیحات (اختیاری)"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)}>انصراف</Button>
          <Button variant="contained" color="error" onClick={handleWithdraw}>
            برداشت
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WalletDashboard;
